import { deserialize } from 'object-deserializer';
import { IApiClient } from '@base/features/api/model/apiClient';
import { Forums, IForumService, Posts, Topics, PlayerInfo, ForumResponse } from './interfaces';
import API_PATHS from '@base/features/api/constants/apiPaths';
import { BehaviorSubject, Subject } from 'rxjs';
import { playerInfoDeserializer } from './deserializer';
import { ILocalStorage } from '@base/features/storage/interfaces';
import { StorageKeys } from '@base/utils/constants';

class ForumService implements IForumService {
  postsList = new Subject<Posts>();
  isModerating = new BehaviorSubject<boolean>(false);

  constructor(private gameId: number, private apiClient: IApiClient, private localStorage: ILocalStorage) {}

  async createTopic(forumId: number, topic: string, message: string): Promise<ForumResponse> {
    const res = await this.apiClient.authGet(
      {
        do: 'thread_add',
        forum: forumId ? forumId : this.gameId,
        topic: topic,
        message: message,
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }

  async createMessage(topicId: number, message: string, subject?: string): Promise<ForumResponse> {
    const res = await this.apiClient.authGet(
      {
        do: 'add',
        thread: topicId,
        Subject: subject,
        message: message,
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }

  async deleteThread(threadId: number): Promise<ForumResponse> {
    const res = await this.apiClient.authGet(
      {
        do: 'thread_del',
        thread: threadId,
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }

  async editThread(
    threadId: number,
    topic: string,
    message: string,
    state: string,
    priority: string
  ): Promise<ForumResponse> {
    const res = await this.apiClient.authGet(
      {
        do: 'thread_edit',
        thread: threadId,
        topic: topic,
        message: message,
        state: state,
        priority: priority,
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }

  async deletePost(postId: string): Promise<ForumResponse> {
    const res = await this.apiClient.authGet(
      {
        do: 'del',
        post: Number(postId),
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }

  async approvePost(postId: string): Promise<ForumResponse> {
    const res = await this.apiClient.authGet(
      {
        do: 'approve',
        post: Number(postId),
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }

  async editPost(postId: string, subject: string, message: string, priority: string): Promise<ForumResponse> {
    const res = await this.apiClient.authGet(
      {
        do: 'edit',
        post: Number(postId),
        Subject: subject,
        message: message,
        priority: priority,
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }

  async getPosterInfo(playerId: string, gameId?: number): Promise<PlayerInfo> {
    const params = {
      gid: gameId ? gameId : this.gameId,
      uid: playerId,
      json: 1,
    };
    const res = await this.apiClient.post(params, API_PATHS.playerInfo);
    const data = await res.json();
    return deserialize(data, playerInfoDeserializer);
  }

  async getForumsList(): Promise<Forums> {
    const res = await this.apiClient.authGet(
      {
        do: 'list',
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }

  async getPostCount(gameId?: string): Promise<number | undefined> {
    const { forums } = await this.getForumsList();
    const postCount = forums.find((forum) => (forum.ForumID == gameId ? gameId : this.gameId))?.POSTSCOUNT;
    return postCount;
  }

  async getPostsList(threadId: number): Promise<void> {
    const res = await this.apiClient.authGet(
      {
        do: 'thread',
        id: threadId,
        json: 1,
      },
      API_PATHS.forum
    );
    const posts = await res.json();
    this.postsList.next(posts);
  }

  async getModerating(): Promise<void> {
    const { moderators } = await this.getForumsList();

    for (const name of moderators) {
      if (name === this.localStorage.get(StorageKeys.SESSION)?.usr) return this.isModerating.next(true);
    }
  }

  async getTopicsListWithSkipNumberOfTopics(gameId?: number, skip?: number): Promise<Topics> {
    const res = await this.apiClient.authGet(
      {
        do: 'forum',
        id: gameId ? gameId : this.gameId,
        skip: skip,
        json: 1,
      },
      API_PATHS.forum
    );
    return await res.json();
  }
}

export default ForumService;
